import { cn } from '@/lib/utils';
import { fmtCurrency } from '@/lib/formatters';
import { semanticTokens } from '@packages/config/theme';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { NetworkIcon } from '@/components/NetworkIcons';
import { Banknote } from 'lucide-react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { DestinationType } from './DestinationTypeTabs';

interface FeeBreakdown {
  amount: number;
  networkFee?: number;
  serviceFee?: number;
  railFee?: number;
  totalFee: number;
  youReceive: number;
  feeDetails?: {
    label: string;
    amount: number;
    description?: string;
  }[];
}

interface WithdrawSummaryCardProps {
  destinationType: DestinationType;
  amount: number;
  currency: string;
  fees: FeeBreakdown;
  // For bank withdrawals
  paymentRail?: string;
  bankName?: string;
  accountLast4?: string;
  estimatedArrival?: string;
  // For crypto withdrawals
  chain?: string;
  walletAddress?: string;
  walletAddressPreview?: string;
  // Status
  isConfirming?: boolean;
  // Cross-currency conversion (Phase 3)
  sourceStablecoin?: string;
  sourceAmount?: number;
  exchangeRate?: number;
}

// Chain display names
const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  polygon: 'Polygon',
  solana: 'Solana',
  base: 'Base',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  avalanche: 'Avalanche',
  tron: 'Tron',
  stellar: 'Stellar',
  bitcoin: 'Bitcoin',
};

// Payment rail labels
const RAIL_LABELS: Record<string, { name: string; eta: string }> = {
  ach: { name: 'ACH Transfer', eta: '1-3 business days' },
  wire: { name: 'Wire Transfer', eta: 'Same day' },
  sepa: { name: 'SEPA Transfer', eta: '1-2 business days' },
  spei: { name: 'SPEI Transfer', eta: 'Minutes' },
  pix: { name: 'PIX Transfer', eta: 'Instant' },
};

/**
 * WithdrawSummaryCard - Premium summary card for withdrawal confirmation
 * 
 * Features:
 * - Liquid Glass styling
 * - Clear fee breakdown
 * - Destination details
 * - Estimated arrival time
 */
export function WithdrawSummaryCard({
  destinationType,
  amount,
  currency,
  fees,
  paymentRail,
  bankName,
  accountLast4,
  estimatedArrival,
  chain,
  walletAddressPreview,
  isConfirming = false,
  sourceStablecoin,
  sourceAmount,
  exchangeRate,
}: WithdrawSummaryCardProps) {
  const railInfo = paymentRail ? RAIL_LABELS[paymentRail.toLowerCase()] : null;
  const chainLabel = chain ? CHAIN_LABELS[chain.toLowerCase()] || chain : '';

  // Check if cross-currency conversion is happening
  const isConversion = sourceStablecoin && sourceAmount && exchangeRate && sourceStablecoin !== currency;

  // Use centralized currency formatter with Bridge API precision
  // Note: formatCurrency is imported from @/lib/formatters as fmtCurrency

  return (
    <div
      className={cn(
        // Liquid Glass Surface tier
        "relative overflow-hidden",
        semanticTokens.radius.xl,
        "bg-gradient-to-br from-white/85 via-white/70 to-white/85",
        "dark:from-slate-900/75 dark:via-slate-800/60 dark:to-slate-900/75",
        "backdrop-blur-lg backdrop-saturate-[180%]",
        "dark:backdrop-saturate-150 dark:brightness-90",
        "border border-white/18 dark:border-white/8",
        "shadow-[0_8px_32px_hsl(220_15%_5%/0.08),inset_0_1px_0_hsl(0_0%_100%/0.5)]",
        "dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.4),inset_0_1px_0_hsl(0_0%_100%/0.1)]",
        semanticTokens.motion.standard,
        isConfirming && "ring-2 ring-primary ring-offset-2"
      )}
    >
      {/* Specular highlight */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20" />
      
      <div className="relative z-10 p-5 space-y-5">
        {/* Header with amount */}
        <div className="text-center space-y-2">
          <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
            You're withdrawing
          </p>
          <div className="flex items-center justify-center gap-2">
            <CurrencyIcon symbol={currency} size="md" />
            <span className={cn(
              "font-display text-display-sm tracking-tight",
              semanticTokens.fg.default
            )}>
              {fmtCurrency(amount, currency)}
            </span>
          </div>
          <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.subtle)}>
            {currency.toUpperCase()}
          </p>
        </div>

        {/* Cross-Currency Conversion Info */}
        {isConversion && (
          <div className={cn(
            "p-3",
            semanticTokens.info.bg,
            semanticTokens.info.border,
            semanticTokens.radius.lg,
            "border",
            "space-y-2"
          )}>
            <div className="flex items-center justify-center gap-2">
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Paying with
              </span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CurrencyIcon currency={sourceStablecoin} size="sm" />
              <span className={cn(
                "font-display text-heading-md tracking-tight",
                semanticTokens.fg.default
              )}>
                {fmtCurrency(sourceAmount, 'USD')}
              </span>
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                {sourceStablecoin}
              </span>
            </div>
            <div className="flex items-center justify-center gap-1 text-xs">
              <span className={cn(semanticTokens.fg.muted)}>
                Rate: 1 {sourceStablecoin} = {exchangeRate.toFixed(4)} {currency}
              </span>
            </div>
          </div>
        )}

        {/* Arrow Divider */}
        <div className="flex items-center justify-center py-2">
          <div className={cn(
            "w-10 h-10 rounded-full",
            "flex items-center justify-center",
            semanticTokens.bg.muted
          )}>
            <SFIcon icon={sfIcons.next} className={cn("w-5 h-5", semanticTokens.fg.muted)} />
          </div>
        </div>

        {/* Destination */}
        <div className={cn(
          "p-4",
          semanticTokens.bg.muted,
          semanticTokens.radius.lg,
          "space-y-3"
        )}>
          <div className="flex items-center gap-2">
            {destinationType === 'bank' ? (
              <Banknote className={cn("w-5 h-5", semanticTokens.fg.muted)} />
            ) : (
              <SFIcon icon={sfIcons.wallet} className={cn("w-5 h-5", semanticTokens.fg.muted)} />
            )}
            <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
              {destinationType === 'bank' ? 'Bank Account' : 'External Wallet'}
            </span>
          </div>

          {destinationType === 'bank' && bankName && (
            <div className="flex items-center justify-between">
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                {bankName}
              </span>
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default, "font-mono")}>
                •••• {accountLast4}
              </span>
            </div>
          )}

          {destinationType === 'crypto' && chain && (
            <>
              <div className="flex items-center justify-between">
                <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                  Network
                </span>
                <div className="flex items-center gap-2">
                  <NetworkIcon network={chain} className="w-4 h-4" />
                  <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                    {chainLabel}
                  </span>
                </div>
              </div>
              {walletAddressPreview && (
                <div className="flex items-center justify-between">
                  <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                    Address
                  </span>
                  <span className={cn(
                    semanticTokens.typography.captionSmall,
                    semanticTokens.fg.default,
                    "font-mono"
                  )}>
                    {walletAddressPreview}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Payment Method for bank */}
          {destinationType === 'bank' && railInfo && (
            <div className="flex items-center justify-between">
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Method
              </span>
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                {railInfo.name}
              </span>
            </div>
          )}
        </div>

        {/* Fee Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <SFIcon icon={sfIcons.info} className={cn("w-4 h-4", semanticTokens.fg.muted)} />
            <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
              Fee Breakdown
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Withdrawal amount
              </span>
              <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                {fmtCurrency(fees.amount, currency)}
              </span>
            </div>

            {/* Dynamic fee details (if provided) */}
            {fees.feeDetails && fees.feeDetails.length > 0 ? (
              fees.feeDetails.map((fee, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex flex-col">
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                      {fee.label}
                    </span>
                    {fee.description && (
                      <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.subtle)}>
                        {fee.description}
                      </span>
                    )}
                  </div>
                  <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                    -{fmtCurrency(fee.amount, currency)}
                  </span>
                </div>
              ))
            ) : (
              // Legacy fee display (backward compatibility)
              <>
                {fees.networkFee !== undefined && fees.networkFee > 0 && (
                  <div className="flex justify-between">
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                      Network fee
                    </span>
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                      -{fmtCurrency(fees.networkFee, currency)}
                    </span>
                  </div>
                )}

                {fees.serviceFee !== undefined && fees.serviceFee > 0 && (
                  <div className="flex justify-between">
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                      Service fee
                    </span>
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                      -{fmtCurrency(fees.serviceFee, currency)}
                    </span>
                  </div>
                )}

                {fees.railFee !== undefined && fees.railFee > 0 && (
                  <div className="flex justify-between">
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                      Transfer fee
                    </span>
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
                      -{fmtCurrency(fees.railFee, currency)}
                    </span>
                  </div>
                )}
              </>
            )}

            {fees.totalFee === 0 && (
              <div className="flex justify-between">
                <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                  Fee
                </span>
                <span className={cn(
                  semanticTokens.typography.body, 
                  semanticTokens.positive.fg,
                  "font-medium"
                )}>
                  Free
                </span>
              </div>
            )}

            {/* Divider */}
            <div className={cn("border-t my-2", semanticTokens.border.default)} />

            {/* Total */}
            <div className="flex justify-between items-center">
              <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                You'll receive
              </span>
              <span className={cn(
                "font-display text-heading-md",
                semanticTokens.fg.default
              )}>
                {fmtCurrency(fees.youReceive, currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Estimated Arrival */}
        <div className={cn(
          "flex items-center gap-3 p-3",
          semanticTokens.info.bg,
          semanticTokens.info.border,
          semanticTokens.radius.lg,
          "border"
        )}>
          <SFIcon icon={sfIcons.pending} className={cn("w-5 h-5 shrink-0", semanticTokens.info.accent)} />
          <div>
            <p className={cn(semanticTokens.typography.label, semanticTokens.info.fg)}>
              Estimated arrival
            </p>
            <p className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
              {destinationType === 'bank' 
                ? (estimatedArrival || railInfo?.eta || '1-3 business days')
                : `~${chainLabel === 'Solana' ? '1 minute' : chainLabel === 'Ethereum' ? '5-15 minutes' : '2-10 minutes'}`}
            </p>
          </div>
        </div>

        {/* Security Note */}
        <div className={cn(
          "flex items-start gap-3 p-3",
          semanticTokens.positive.bg,
          semanticTokens.positive.border,
          semanticTokens.radius.lg,
          "border"
        )}>
          <SFIcon icon={sfIcons.security} className={cn("w-5 h-5 shrink-0 mt-0.5", semanticTokens.positive.accent)} />
          <div>
            <p className={cn(semanticTokens.typography.label, semanticTokens.positive.fg)}>
              Secure Transaction
            </p>
            <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
              {destinationType === 'bank' 
                ? 'Your withdrawal is protected by bank-grade encryption and fraud monitoring.'
                : 'Please verify the wallet address carefully. Crypto transactions are irreversible.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact inline summary for confirmation button area
 */
interface WithdrawSummaryInlineProps {
  amount: number;
  currency: string;
  fee: number;
  youReceive: number;
  isHighFee?: boolean;
  isCriticalFee?: boolean;
  suggestedNetwork?: string;
  potentialSavings?: number;
}

export function WithdrawSummaryInline({
  amount,
  currency,
  fee,
  youReceive,
  isHighFee,
  isCriticalFee,
  suggestedNetwork,
  potentialSavings,
}: WithdrawSummaryInlineProps) {
  const feePercentage = amount > 0 ? (fee / amount) * 100 : 0;
  
  return (
    <div className="space-y-2">
      {/* High fee warning */}
      {(isHighFee || isCriticalFee) && (
        <div className={cn(
          "flex items-start gap-2 p-3",
          isCriticalFee ? semanticTokens.danger.bg : semanticTokens.warning.bg,
          semanticTokens.radius.lg
        )}>
          <SFIcon icon={sfIcons.alertTriangle} className={cn(
            "w-4 h-4 mt-0.5 shrink-0",
            isCriticalFee ? semanticTokens.danger.accent : semanticTokens.warning.accent
          )} />
          <div className={semanticTokens.typography.bodySmall}>
            <span className={cn(
              "font-medium",
              isCriticalFee ? semanticTokens.danger.fg : semanticTokens.warning.fg
            )}>
              High network fee ({feePercentage.toFixed(0)}% of amount)
            </span>
            {suggestedNetwork && potentialSavings && (
              <p className={cn(
                "mt-0.5",
                isCriticalFee ? semanticTokens.danger.fg : semanticTokens.warning.fg
              )}>
                💡 Use {suggestedNetwork.charAt(0).toUpperCase() + suggestedNetwork.slice(1)} to save ~${potentialSavings.toFixed(2)}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* You'll receive */}
      <div className={cn(
        "flex items-center justify-between p-3",
        semanticTokens.bg.muted,
        semanticTokens.radius.lg
      )}>
        <div className="flex items-center gap-2">
          <SFIcon icon={sfIcons.success} className={cn("w-4 h-4", semanticTokens.positive.accent)} />
          <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            You'll receive:
          </span>
        </div>
        <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default, "font-semibold")}>
          {fmtCurrency(youReceive, currency)}
        </span>
      </div>
    </div>
  );
}

